import React, { useEffect } from 'react';

import { CONTACT_INFO } from '@constants/contact';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: any;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = `${CONTACT_INFO.company.name} - ${CONTACT_INFO.company.tagline}`,
  description = `Start your wealth building journey with ${CONTACT_INFO.contact_person.full_name} - ${CONTACT_INFO.contact_person.designation}. Get personalized investment advice, goal-based planning, and expert guidance for SIP investments. Free consultation available.`,
  keywords = 'mutual funds, SIP investment, wealth building, financial planning, AMFI certified, investment advisor, goal based investing, asset allocation, FIRE, retirement planning, tax saving, equity funds, debt funds, portfolio management, financial consultant, investment guidance, systematic investment plan',
  canonicalUrl = `${CONTACT_INFO.social.website}/`,
  ogImage = `${CONTACT_INFO.social.website}/og-image.png`,
  ogType = 'website',
  structuredData,
}) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property
        ? `meta[property="${name}"]`
        : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Update canonical link
    const updateCanonical = (url: string) => {
      let canonical = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement;

      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }

      canonical.setAttribute('href', url);
    };

    // Update basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:type', ogType, true);

    // Update Twitter tags
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', ogImage, true);

    // Update canonical URL
    updateCanonical(canonicalUrl);

    // Add structured data if provided
    if (structuredData) {
      const existingScript = document.getElementById('dynamic-structured-data');
      if (existingScript) {
        existingScript.remove();
      }

      const script = document.createElement('script');
      script.id = 'dynamic-structured-data';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      if (structuredData) {
        const script = document.getElementById('dynamic-structured-data');
        if (script) script.remove();
      }
    };
  }, [
    title,
    description,
    keywords,
    canonicalUrl,
    ogImage,
    ogType,
    structuredData,
  ]);

  return null; // This component doesn't render anything
};

export default SEOHead;
